import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Alert } from 'react-bootstrap';

import ChartComSliders from 'components/Windx/ChartComSliders';
import AvisoFiltrosAlterados from 'components/Windx/AvisoFiltrosAlterados';

import { useGetPainelOptionsQuery, useLazyGetChartDataSMQuery } from 'services/api/windx';
import {
  ChartRequestSM,
  EChartsOption,
  TipoRodada,
  painelExibeTipoRodada,
} from 'helper/windx';
import { derivarSemanasFuturasSM } from 'helper/windx/derivarSemanasFuturasSM';
import {
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxPatamar,
  selectWindxUnidade,
  selectWindxFatorRen,
  selectWindxDataSimulacao,
  selectWindxTelaAtiva,
  selectWindxTriggerConsulta,
  selectWindxConsultaPendente,
} from 'redux/reducers/windxControlPanelReducer';

/**
 * Grafico ECharts da tela Weol-SM (semanal).
 *
 * Le filtros do redux, dispara `getChartDataSM` quando todos os
 * obrigatorios estao presentes (subdivisao + >=1 entidade + >=1 modelo +
 * Tipo de Rodada, WEOL-SM #99) e renderiza o `option` que o backend ja
 * entrega pronto (Phase A, WINDX-009).
 *
 * Estados (mesmo padrao do <GraficoSH>):
 * - skip   : faltam filtros obrigatorios -> placeholder de instrucao
 * - loading: spinner
 * - error  : Alert vermelho com mensagem do backend
 * - empty  : `total_pontos === 0` -> Alert info
 * - ok     : <ReactECharts option={data.option} />
 *
 * Phase B (FE-WINDX-014): patamar agora vem do redux (Media/L/M/P) e
 * unidade do redux (mw/fc). Backend (BE !65) ja aceita L/M/P em SM.
 */
interface GraficoSMProps {
  // Espelha `isFetching` para fora sem mudar nada do render — a BarraConsulta
  // do mobile precisa do estado da consulta, que so este componente conhece.
  // Desktop nao passa: o default no-op mantem o comportamento identico.
  onLoadingChange?: (carregando: boolean) => void;
  // Abre o grafico em tela cheia. So o mobile passa — vira o botao de
  // expandir no cabecalho do card do `ChartComSliders`.
  onExpandir?: () => void;
}

export default function GraficoSM({ onLoadingChange, onExpandir }: GraficoSMProps = {}) {
  const subdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const patamar = useSelector(selectWindxPatamar);
  const unidade = useSelector(selectWindxUnidade);
  const fatorRen = useSelector(selectWindxFatorRen);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const triggerConsulta = useSelector(selectWindxTriggerConsulta);
  const consultaPendente = useSelector(selectWindxConsultaPendente);

  // WEOL-SM (#99): o horizonte (`semanas_futuras`) sai do `dias_previsao` dos
  // modelos marcados, que vem do painel-options do SM. E a mesma query (mesmo
  // argumento) que o painel de controle ja assina, entao nao ha request extra.
  const { data: painelSM } = useGetPainelOptionsQuery('sm');
  // Sem o painel (ainda carregando ou com erro), vale o default 5 do BE: a
  // consulta nao espera pelo horizonte — o painel chega antes de o usuario
  // conseguir marcar modelos, porque e ele que lista os modelos.
  const semanasFuturas = useMemo(
    () => derivarSemanasFuturasSM(idsModelos, painelSM?.modelos ?? []),
    [idsModelos, painelSM],
  );

  // WEOL-SM (#99): o Tipo de Rodada tambem e obrigatorio, como no SH. O botao e
  // os auto-disparos ja exigem (selectWindxFiltrosCompletos); este gate local
  // impede um trigger sem tipo de consultar sem `tipo_rodada`. Com a flag do BE
  // desligada, o useControlPanelRules assume 'final'.
  const filtrosCompletos = (
    typeof subdivisao === 'number'
    && idsEntidades.length > 0
    && idsModelos.length > 0
    && !!tipoRodada
  );

  const payload = useMemo<ChartRequestSM | undefined>(() => {
    if (!filtrosCompletos) return undefined;
    return {
      subdivisao_espacial: subdivisao as number,
      id_entidades: idsEntidades,
      id_modelos: idsModelos,
      unidade,
      patamar,
      // WINDX-030: WEOL-SM mantem com_rmv: bool (chart-data-sm NAO mudou).
      // Redux passou a guardar opcoesRmv como array; WEOL-SM tem
      // apenasComRmv=true (sempre length=1), entao basta checar inclusao.
      com_rmv: opcoesRmv.includes('com_rmv'),
      // WEOL-SM (#99, contrato sm/chart-data v4.1): rodada preliminar ou final
      // de todos os modelos do request.
      tipo_rodada: tipoRodada as TipoRodada,
      // 1..10: ceil(max(dias_previsao dos marcados) / 7). O ONS e os modelos de
      // Medio Prazo (35 dias) seguem com 5; os dois da Climatologia (70 dias)
      // pedem 10. O BE corta pelas N primeiras semanas da rodada de cada modelo.
      semanas_futuras: semanasFuturas,
      // Padronizacao REN (2026-05-28): overlay opt-in da serie REN (media +
      // patamares conforme `patamar`). GraficoSM so dispara em telaAtiva==='sm'
      // (Semanal); Comparativo usa TabelaComparativoSM, sem REN.
      incluir_fator_ren: fatorRen,
      ...(dataSimulacao ? { data_simulacao: dataSimulacao } : {}),
    };
  }, [
    filtrosCompletos,
    subdivisao,
    idsEntidades,
    idsModelos,
    unidade,
    patamar,
    opcoesRmv,
    tipoRodada,
    semanasFuturas,
    fatorRen,
    dataSimulacao,
  ]);

  const [getChart, {
    data, isFetching, isError, error,
  }] = useLazyGetChartDataSMQuery();

  const lastTrigger = useRef(0);

  // Bug do PO 2026-05-18 #4 (v2): adicionado gate `telaAtiva === 'sm'`.
  //
  // Causa raiz: ao trocar de aba (ex: WEOL-SH -> WEOL-SM), React monta o
  // GraficoSM antes do useEffect do RouteWrappers/Windx disparar
  // `setTelaAtiva('sm')`. No primeiro render, o Redux ainda contem:
  //  - filtros do SH (idsModelos, idSubdivisao, idsEntidades, etc.)
  //  - triggerConsulta = ultimo valor do SH (>0)
  //  - telaAtiva ainda = 'sh'
  //
  // O `payload` calculado eh valido (filtros SH passam pela schema do SM),
  // o useEffect roda, consome `lastTrigger.current = triggerConsulta_SH` e
  // dispara um request `sm/chart/data/` com payload SH — invariavelmente
  // retornando `total_pontos=0`.
  //
  // Quando `setTelaAtiva('sm')` finalmente roda, reseta tudo mas
  // `lastTrigger.current` ja foi consumido — o primeiro clique do usuario
  // em "Realizar Consulta" (que so incrementa triggerConsulta para 1 a
  // partir do reset) nao passa pela guarda `triggerConsulta > lastTrigger`.
  // O segundo clique consegue (triggerConsulta=2 > 1) e ai dispara.
  //
  // Fix: gate explicito `telaAtiva === 'sm'`. Enquanto o Redux nao confirma
  // que a tela atual e SM, NENHUM request eh disparado — protege contra o
  // "leak" entre abas no momento de mount.
  useEffect(() => {
    // Defesa em profundidade (WINDX-bug-limpar-filtros, 2026-05-26): se o
    // counter regrediu (resetado para 0 por algum reducer), re-sincronizamos
    // o ref. Fix primario eh no reducer (preserva triggerConsulta no reset);
    // este gate protege contra regressao futura.
    if (triggerConsulta === 0) {
      lastTrigger.current = 0;
      return;
    }
    if (telaAtiva !== 'sm') return;
    if (triggerConsulta <= lastTrigger.current) return;
    if (!payload) return;
    lastTrigger.current = triggerConsulta;
    getChart(payload);
  }, [telaAtiva, triggerConsulta, payload, getChart]);

  // Acima dos early-returns de proposito: hooks nao podem viver abaixo deles.
  useEffect(() => {
    onLoadingChange?.(isFetching);
  }, [isFetching, onLoadingChange]);

  if (!filtrosCompletos) {
    // WEOL-SM (#99): o aviso so pede o Tipo de Rodada quando o painel tem a etapa
    // (flag do BE ligada); sem ela o painel assume 'final' e nao ha o que escolher.
    const textoFiltrosIncompletos = painelExibeTipoRodada(painelSM, 'sm')
      ? 'Selecione subdivisão, ao menos uma entidade, o Tipo de Rodada e um modelo para gerar o gráfico semanal.'
      : 'Selecione subdivisão, ao menos uma entidade e um modelo para gerar o gráfico semanal.';
    return (
      <div className="mt-4 p-4 border rounded text-muted text-center">
        {textoFiltrosIncompletos}
      </div>
    );
  }

  if (consultaPendente) {
    return <AvisoFiltrosAlterados />;
  }

  if (isFetching) {
    return (
      <div className="mt-4 p-5 text-center" data-testid="windx-grafico-sm-loading">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">Carregando gráfico semanal…</div>
      </div>
    );
  }

  if (isError) {
    const apiMessage = (error as { data?: { description?: string; message?: string } } | undefined)
      ?.data?.description
      || (error as { data?: { description?: string; message?: string } } | undefined)?.data?.message
      || 'Falha ao carregar dados do gráfico semanal.';
    return (
      <Alert variant="danger" className="mt-4" data-testid="windx-grafico-sm-error">
        {apiMessage}
      </Alert>
    );
  }

  if (data && data.metadata.total_pontos === 0) {
    return (
      <Alert variant="info" className="mt-4" data-testid="windx-grafico-sm-empty">
        Nenhum dado disponível para os filtros selecionados.
      </Alert>
    );
  }

  if (!data) return null;

  const option = data.option as EChartsOption;
  return (
    // WINDX-040 Frente B (revisao 2026-05-25): height:32rem como base
    // (igual ao comportamento original) + flex-grow-1 para crescer quando
    // pai for flex container.
    <div
      className="mt-4 position-relative w-100 flex-grow-1"
      style={{ height: '32rem' }}
      data-testid="windx-grafico-sm-chart"
    >
      <ChartComSliders
        className="h-100"
        option={option}
        kicker="WEOL-SM · Semanal"
        onExpandir={onExpandir}
      />
    </div>
  );
}
